import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, filter, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { HubContextService } from './hub-context.service';
import { HubBrief } from '../models';
import {
  HubContextInitialState,
  HubContextListLoad,
  HubContextSelectItem,
  HubContextUpdateSelectedItem,
} from './hub-context.actions';
import { AuthFacade, AuthUserData } from '../../../auth';
import { Error, CustomHttpErrorResponse, TimezoneTypeEnum } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface HubContextStateModel {
  items: HubBrief[];
  selectedItem: HubBrief;
  contextTimezone: string;
  loaded: boolean;
}

const initialState: HubContextStateModel = {
  items: [],
  selectedItem: null,
  contextTimezone: null,
  loaded: false,
};

@State<HubContextStateModel>({
  name: 'hubContext',
  defaults: initialState,
})
@Injectable()
export class HubContextState {
  constructor(
    private hubContextService: HubContextService,
    private notificationService: NotificationService,
    private authFacade: AuthFacade,
  ) {}

  @Selector()
  static getItems(state: HubContextStateModel): HubBrief[] {
    return state.items;
  }

  @Selector()
  static getActiveItems(state: HubContextStateModel): HubBrief[] {
    return this.getItems(state).filter((item: HubBrief) => item.active);
  }

  @Selector()
  static getSelectedItem(state: HubContextStateModel): HubBrief {
    return state.selectedItem;
  }

  @Selector()
  static getContextTimezone(state: HubContextStateModel): string {
    return state.contextTimezone;
  }

  @Selector()
  static getLoaded(state: HubContextStateModel): boolean {
    return state.loaded;
  }

  @Action(HubContextListLoad)
  getAvailableHubs(ctx: StateContext<HubContextStateModel>): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubContextService.getHubList().pipe(
      map((items: HubBrief[]) => {
        ctx.patchState({ items, loaded: true });

        if (items?.length) {
          ctx.dispatch(new HubContextUpdateSelectedItem());
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubContextUpdateSelectedItem)
  updateSelectedHubContext(ctx: StateContext<HubContextStateModel>): void {
    const items = ctx.getState().items || [];
    const activeItems = items.filter((item: HubBrief) => item?.active);
    const lastSelectedItem = ctx.getState().selectedItem;
    const hubContextId = this.hubContextService.getHubContextIdFromStorage();
    const hubContext = this.getHubContextFromId(ctx, hubContextId);

    if (lastSelectedItem) {
      return;
    }
    if (hubContext?.active) {
      ctx.dispatch(new HubContextSelectItem(hubContext));
    } else if (activeItems.length) {
      ctx.dispatch(new HubContextSelectItem(activeItems[0]));
    }
  }

  @Action(HubContextSelectItem)
  setHubContext(ctx: StateContext<HubContextStateModel>, { payload }: HubContextSelectItem): void {
    this.hubContextService.setHubContextIdToStorage(payload);
    ctx.patchState({
      selectedItem: payload,
      loaded: true,
    });

    this.authFacade.userData$.pipe(filter(Boolean), take(1)).subscribe(({ renderTimezone, timezone }: AuthUserData) => {
      const contextTimezone = renderTimezone === TimezoneTypeEnum.Selected ? timezone : payload.timezone;
      ctx.patchState({ contextTimezone });
    });
  }

  @Action(HubContextInitialState)
  setInitialState(ctx: StateContext<HubContextStateModel>): void {
    ctx.setState(initialState);
  }

  private getHubContextFromId(ctx: StateContext<HubContextStateModel>, id: number): HubBrief {
    const items = ctx.getState().items;

    return items.find((item: HubBrief) => item.id === id);
  }
}
